import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import {
  Plus, Search, RefreshCw, Eye, Truck, Package, AlertTriangle,
  CheckCircle, Clock, Calendar, Camera, Upload, Trash2, FileText,
  ChevronRight, MapPin, Phone, User, ClipboardCheck, DollarSign,
  AlertCircle, XCircle, Image as ImageIcon
} from 'lucide-react';
import {
  getCompanies, getDeliveryNotesV2,
  createDemobRequest, getDemobList, getDemobDetail, scheduleDemob,
  startDemobInspection, cancelDemob, addDemobInspection, getDemobInspections,
  updateDemobInspection, addDemobDamage, getDemobDamages, updateDemobDamage,
  deleteDemobDamage, approveDemobDamage, rejectDemobDamage, setDemobDamageBillable,
  uploadDemobPhoto, getDemobPhotos, getDemobPhoto, deleteDemobPhoto,
  completeDemobInspection, generateDemobFinalInvoice, completeDemob, getDemobStats,
  getDemobReadiness,
} from '../lib/api';
import { useAuth } from '../context/AuthContext';

const statusColors = {
  requested: 'bg-yellow-100 text-yellow-800',
  scheduled: 'bg-blue-100 text-blue-800',
  in_progress: 'bg-purple-100 text-purple-800',
  pending_invoice: 'bg-orange-100 text-orange-800',
  completed: 'bg-green-100 text-green-800',
  cancelled: 'bg-gray-100 text-gray-800'
};

const conditionColors = {
  good: 'bg-green-100 text-green-800',
  fair: 'bg-yellow-100 text-yellow-800',
  damaged: 'bg-red-100 text-red-800',
  needs_repair: 'bg-orange-100 text-orange-800'
};

const severityColors = {
  minor: 'bg-yellow-100 text-yellow-800',
  moderate: 'bg-orange-100 text-orange-800',
  major: 'bg-red-100 text-red-800',
  critical: 'bg-red-200 text-red-900'
};

export const DemobilizationPage = () => {
  const [companies, setCompanies] = useState([]);
  const [demobs, setDemobs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusTab, setStatusTab] = useState('requested');
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [inspectionDialogOpen, setInspectionDialogOpen] = useState(false);
  const [damageDialogOpen, setDamageDialogOpen] = useState(false);
  const [completeInspectionDialogOpen, setCompleteInspectionDialogOpen] = useState(false);
  
  // Selected demob
  const [selectedDemob, setSelectedDemob] = useState(null);
  const [demobDetail, setDemobDetail] = useState(null);
  
  // Delivery notes for creating demob
  const [deliveryNotes, setDeliveryNotes] = useState([]);
  const [selectedDN, setSelectedDN] = useState(null);
  
  // Form states
  const [createForm, setCreateForm] = useState({
    trigger: 'admin_action',
    requested_return_date: '',
    return_location: '',
    transport_required: false,
    client_contact_name: '',
    client_contact_phone: '',
    reason: '',
    notes: ''
  });
  
  const [scheduleForm, setScheduleForm] = useState({
    scheduled_return_date: '',
    return_location: '',
    transport_required: false,
    transport_vehicle: '',
    transport_driver: '',
    transport_notes: ''
  });
  
  const [inspectionForm, setInspectionForm] = useState({
    item_index: 0,
    item_description: '',
    asset_id: '',
    deployment_condition: '',
    return_condition: 'good',
    condition_notes: '',
    deployment_hours: '',
    return_hours: ''
  });
  
  const [damageForm, setDamageForm] = useState({
    inspection_item_id: '',
    // Basic classification
    category: 'mechanical',
    severity: 'minor',
    description: '',
    location_on_asset: '',
    estimated_repair_cost: 0,
    repair_required: true,
    // Enhanced enterprise fields
    inspection_item: 'other',
    damage_type: 'other',
    quantity: 1,
    actual_repair_cost: '',
    repair_vendor: '',
    insurance_claim: false,
    warranty_applicable: false,
    priority: 'medium',
    internal_notes: '',
  });
  
  const [completeForm, setCompleteForm] = useState({
    overall_condition: 'good',
    inspection_notes: '',
    client_signed_by: ''
  });
  
  const photoInputRef = useRef(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [damagePhotoFiles, setDamagePhotoFiles] = useState([]);
  const [readiness, setReadiness] = useState({ ready: false, missing: [], counts: {} });
  const [lightbox, setLightbox] = useState({ open: false, photos: [], index: 0, current: null });
  const [zoom, setZoom] = useState(1);
  const { user: currentUser } = useAuth();
  const role = currentUser?.role;
  const isOpsManager = role === 'operation_manager' || role === 'admin';
  const isFinance = role === 'finance' || role === 'finance_manager' || role === 'admin';

  // Fetch data
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [companiesRes, demobsRes, statsRes] = await Promise.all([
        getCompanies(),
        getDemobList({ company_id: selectedCompany, status: statusFilter }),
        getDemobStats(selectedCompany)
      ]);
      setCompanies(companiesRes.data);
      setDemobs(demobsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  }, [selectedCompany, statusFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Fetch delivered DNs for creating demob
  const fetchDeliveredDNs = async () => {
    try {
      const response = await getDeliveryNotesV2({ status: 'delivered' });
      // Filter out DNs that already have active demobs
      const activeDemobDNs = demobs.filter(d => d.status !== 'cancelled').map(d => d.delivery_note_id);
      const availableDNs = response.data.filter(dn => !activeDemobDNs.includes(dn.id));
      setDeliveryNotes(availableDNs);
    } catch (error) {
      toast.error('Failed to fetch delivery notes');
    }
  };

  // Fetch demob detail
  const fetchDemobDetail = async (demobId) => {
    try {
      const response = await getDemobDetail(demobId);
      setDemobDetail(response.data);
    } catch (error) {
      toast.error('Failed to fetch demob details');
    }
  };

  // Create demob
  const handleCreateDemob = async () => {
    if (!selectedDN) {
      toast.error('Please select a delivery note');
      return;
    }
    try {
      await createDemobRequest({
        delivery_note_id: selectedDN.id,
        ...createForm
      });
      toast.success('Demobilization request created');
      setCreateDialogOpen(false);
      setSelectedDN(null);
      setCreateForm({
        trigger: 'admin_action',
        requested_return_date: '',
        return_location: '',
        transport_required: false,
        client_contact_name: '',
        client_contact_phone: '',
        reason: '',
        notes: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create demob request');
    }
  };

  // Schedule demob
  const handleScheduleDemob = async () => {
    try {
      await scheduleDemob(selectedDemob.id, scheduleForm);
      toast.success('Demob scheduled');
      setScheduleDialogOpen(false);
      fetchData();
      if (viewDialogOpen) fetchDemobDetail(selectedDemob.id);
    } catch (error) {
      toast.error('Failed to schedule demob');
    }
  };

  // Start inspection
  const handleStartInspection = async () => {
    try {
      await startDemobInspection(selectedDemob.id);
      toast.success('Inspection started');
      fetchData();
      fetchDemobDetail(selectedDemob.id);
    } catch (error) {
      toast.error('Failed to start inspection');
    }
  };

  // Add inspection item
  const handleAddInspection = async () => {
    try {
      await addDemobInspection(demobDetail.id, {
        ...inspectionForm,
        deployment_hours: inspectionForm.deployment_hours ? parseFloat(inspectionForm.deployment_hours) : null,
        return_hours: inspectionForm.return_hours ? parseFloat(inspectionForm.return_hours) : null
      });
      toast.success('Inspection item added');
      setInspectionDialogOpen(false);
      setInspectionForm({
        item_index: 0,
        item_description: '',
        asset_id: '',
        deployment_condition: '',
        return_condition: 'good',
        condition_notes: '',
        deployment_hours: '',
        return_hours: ''
      });
      fetchDemobDetail(demobDetail.id);
    } catch (error) {
      toast.error('Failed to add inspection');
    }
  };

  // Add damage
  const handleAddDamage = async () => {
    if (!damageForm.description?.trim()) {
      toast.error('Description is required');
      return;
    }
    if (!damagePhotoFiles || damagePhotoFiles.length === 0) {
      toast.error('Attach at least one photo before saving damage');
      return;
    }
    try {
      const payload = {
        ...damageForm,
        estimated_repair_cost: parseFloat(damageForm.estimated_repair_cost) || 0,
        actual_repair_cost: damageForm.actual_repair_cost === '' ? null : (parseFloat(damageForm.actual_repair_cost) || 0),
        quantity: parseInt(damageForm.quantity || 1, 10) || 1,
      };
      // Never send billable_to_client from inspector — backend forces false anyway
      delete payload.billable_to_client;
      delete payload.charge_to;
      const res = await addDemobDamage(demobDetail.id, payload);
      const damageId = res?.data?.id;
      // Upload each attached photo linked to the newly-created damage
      if (damageId && damagePhotoFiles?.length) {
        for (const f of damagePhotoFiles) {
          try {
            await uploadDemobPhoto(demobDetail.id, f, 'damage', null, damageId, damageForm.description?.slice(0, 100));
          } catch (err) {
            toast.error(`Photo upload failed: ${f.name}`);
          }
        }
      }
      toast.success('Damage recorded — awaiting Ops Manager review');
      setDamageDialogOpen(false);
      setDamagePhotoFiles([]);
      setDamageForm({
        inspection_item_id: '',
        category: 'mechanical', severity: 'minor', description: '',
        location_on_asset: '', estimated_repair_cost: 0, repair_required: true,
        inspection_item: 'other', damage_type: 'other', quantity: 1,
        actual_repair_cost: '', repair_vendor: '',
        insurance_claim: false, warranty_applicable: false,
        priority: 'medium', internal_notes: '',
      });
      fetchDemobDetail(demobDetail.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record damage');
    }
  };

  // Upload photo (single or multiple)
  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploadingPhoto(true);
    try {
      for (const file of files) {
        setUploadProgress(0);
        await uploadDemobPhoto(
          demobDetail.id, file, 'return_condition', null, null, '',
          (evt) => {
            if (evt.total) setUploadProgress(Math.round((evt.loaded * 100) / evt.total));
          }
        );
        toast.success(`Uploaded: ${file.name}`);
      }
      fetchDemobDetail(demobDetail.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
      setUploadProgress(0);
      e.target.value = '';
    }
  };

  const handleDeletePhoto = async (photoId) => {
    if (!window.confirm('Delete this photo?')) return;
    try {
      await deleteDemobPhoto(demobDetail.id, photoId);
      toast.success('Photo deleted');
      fetchDemobDetail(demobDetail.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Delete failed');
    }
  };

  const openPhotoLightbox = async (photo, allPhotos) => {
    try {
      const res = await getDemobPhoto(demobDetail.id, photo.id);
      setLightbox({
        open: true,
        photos: allPhotos,
        index: allPhotos.findIndex(p => p.id === photo.id),
        current: { ...photo, dataUrl: `data:${res.data.mime_type || 'image/jpeg'};base64,${res.data.data}`, uploaded_by_email: res.data.uploaded_by_email, uploaded_at: res.data.uploaded_at, media_kind: res.data.media_kind },
      });
    } catch (err) {
      toast.error('Failed to load photo');
    }
  };

  const navLightbox = async (delta) => {
    if (!lightbox.open) return;
    const next = lightbox.index + delta;
    if (next < 0 || next >= lightbox.photos.length) return;
    const nextPhoto = lightbox.photos[next];
    try {
      const res = await getDemobPhoto(demobDetail.id, nextPhoto.id);
      setLightbox((s) => ({
        ...s,
        index: next,
        current: { ...nextPhoto, dataUrl: `data:${res.data.mime_type || 'image/jpeg'};base64,${res.data.data}`, uploaded_by_email: res.data.uploaded_by_email, uploaded_at: res.data.uploaded_at, media_kind: res.data.media_kind },
      }));
    } catch (err) {
      toast.error('Failed to load photo');
    }
  };

  // Complete inspection
  const handleCompleteInspection = async () => {
    try {
      await completeDemobInspection(demobDetail.id, completeForm);
      toast.success('Inspection completed');
      setCompleteInspectionDialogOpen(false);
      fetchData();
      fetchDemobDetail(demobDetail.id);
    } catch (error) {
      toast.error('Failed to complete inspection');
    }
  };

  // Generate final invoice
  const handleGenerateFinalInvoice = async () => {
    try {
      const response = await generateDemobFinalInvoice(demobDetail.id, true);
      if (response.data.invoice) {
        toast.success(`Invoice ${response.data.invoice.invoice_number} generated`);
      } else {
        toast.info(response.data.message);
      }
      fetchDemobDetail(demobDetail.id);
    } catch (error) {
      toast.error('Failed to generate invoice');
    }
  };

  // Complete demob
  const handleCompleteDemob = async () => {
    try {
      await completeDemob(demobDetail.id);
      toast.success('Demobilization completed');
      setViewDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete demob');
    }
  };

  // Cancel demob
  const handleCancelDemob = async () => {
    if (!window.confirm('Are you sure you want to cancel this demobilization?')) return;
    try {
      await cancelDemob(selectedDemob.id, 'Cancelled by admin');
      toast.success('Demob cancelled');
      setViewDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to cancel demob');
    }
  };

  // View demob
  const handleViewDemob = (demob) => {
    setSelectedDemob(demob);
    fetchDemobDetail(demob.id);
    setViewDialogOpen(true);
  };

  // Filter demobs by search + status tab
  const filteredDemobs = demobs.filter(d => {
    if (statusTab !== 'all') {
      if (statusTab === 'damages') {
        if (!(d.status === 'in_progress' && d.has_damages)) return false;
      } else if (statusTab === 'closed') {
        if (!['completed', 'cancelled'].includes(d.status)) return false;
      } else if (d.status !== statusTab) {
        return false;
      }
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return d.demob_number?.toLowerCase().includes(q) ||
             d.dn_number?.toLowerCase().includes(q) ||
             d.client_name?.toLowerCase().includes(q);
    }
    return true;
  });

  const tabCounts = demobs.reduce((acc, d) => {
    acc.all = (acc.all || 0) + 1;
    if (d.status === 'requested') acc.requested = (acc.requested || 0) + 1;
    if (d.status === 'scheduled') acc.scheduled = (acc.scheduled || 0) + 1;
    if (d.status === 'in_progress') acc.in_progress = (acc.in_progress || 0) + 1;
    if (d.status === 'in_progress' && d.has_damages) acc.damages = (acc.damages || 0) + 1;
    if (d.status === 'pending_invoice') acc.pending_invoice = (acc.pending_invoice || 0) + 1;
    if (['completed', 'cancelled'].includes(d.status)) acc.closed = (acc.closed || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-black tracking-tight">Demobilization</h1>
          <p className="text-muted-foreground">Manage equipment returns and off-hire process</p>
        </div>
        <Button onClick={() => { fetchDeliveredDNs(); setCreateDialogOpen(true); }}>
          <Plus className="h-4 w-4 mr-2" />
          Create Demob Request
        </Button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">Requested</p>
              <p className="text-2xl font-bold text-yellow-600">{stats.status_counts?.requested || 0}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">Scheduled</p>
              <p className="text-2xl font-bold text-blue-600">{stats.status_counts?.scheduled || 0}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">In Progress</p>
              <p className="text-2xl font-bold text-purple-600">{stats.status_counts?.in_progress || 0}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">Pending Invoice</p>
              <p className="text-2xl font-bold text-orange-600">{stats.status_counts?.pending_invoice || 0}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">Completed</p>
              <p className="text-2xl font-bold text-green-600">{stats.status_counts?.completed || 0}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">Total Damages</p>
              <p className="text-2xl font-bold text-red-600">SAR {stats.total_damage_cost?.toLocaleString() || 0}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search demob#, DN#, client..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={selectedCompany || 'all'} onValueChange={(v) => setSelectedCompany(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="All Companies" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Companies</SelectItem>
                {companies.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={statusFilter || 'all'} onValueChange={(v) => setStatusFilter(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="requested">Requested</SelectItem>
                <SelectItem value="scheduled">Scheduled</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="pending_invoice">Pending Invoice</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={fetchData} disabled={loading}>
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Demob List */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle>Operations Workflow</CardTitle>
          <Tabs value={statusTab} onValueChange={setStatusTab} className="mt-3">
            <TabsList className="grid grid-cols-6 w-full">
              <TabsTrigger value="requested" data-testid="tab-pending-requests">Pending ({tabCounts.requested || 0})</TabsTrigger>
              <TabsTrigger value="scheduled" data-testid="tab-schedule">Schedule ({tabCounts.scheduled || 0})</TabsTrigger>
              <TabsTrigger value="in_progress" data-testid="tab-inspect">Inspect ({tabCounts.in_progress || 0})</TabsTrigger>
              <TabsTrigger value="damages" data-testid="tab-damages">Damages ({tabCounts.damages || 0})</TabsTrigger>
              <TabsTrigger value="pending_invoice" data-testid="tab-complete">Complete ({tabCounts.pending_invoice || 0})</TabsTrigger>
              <TabsTrigger value="closed" data-testid="tab-closed">Closed ({tabCounts.closed || 0})</TabsTrigger>
            </TabsList>
          </Tabs>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Demob #</TableHead>
                <TableHead>DN #</TableHead>
                <TableHead>Client</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Condition</TableHead>
                <TableHead>Damages</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredDemobs.map(demob => (
                <TableRow key={demob.id}>
                  <TableCell className="font-medium">{demob.demob_number}</TableCell>
                  <TableCell>{demob.dn_number}</TableCell>
                  <TableCell>{demob.client_name}</TableCell>
                  <TableCell className="capitalize">{demob.dn_type}</TableCell>
                  <TableCell>
                    <Badge className={statusColors[demob.status]}>{demob.status.replace('_', ' ')}</Badge>
                  </TableCell>
                  <TableCell>
                    {demob.overall_condition ? (
                      <Badge className={conditionColors[demob.overall_condition]}>{demob.overall_condition}</Badge>
                    ) : '-'}
                  </TableCell>
                  <TableCell>
                    {demob.has_damages ? (
                      <span className="text-red-600 font-medium">
                        {demob.damage_count} (SAR {demob.total_damage_cost?.toLocaleString()})
                      </span>
                    ) : '-'}
                  </TableCell>
                  <TableCell>{new Date(demob.created_at).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <Button size="sm" variant="outline" onClick={() => handleViewDemob(demob)}>
                      <Eye className="h-4 w-4 mr-1" />
                      View
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {filteredDemobs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                    No demobilization requests found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Create Demobilization Request</DialogTitle>
            <DialogDescription>Start the off-hire process for a delivered equipment</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            <div>
              <Label>Select Delivery Note *</Label>
              <Select value={selectedDN?.id || ''} onValueChange={(v) => setSelectedDN(deliveryNotes.find(d => d.id === v))}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a delivered DN" />
                </SelectTrigger>
                <SelectContent>
                  {deliveryNotes.map(dn => (
                    <SelectItem key={dn.id} value={dn.id}>
                      {dn.dn_number} - {dn.client_name} ({dn.dn_type})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {selectedDN && (
              <Card className="bg-slate-50">
                <CardContent className="p-4">
                  <p className="font-medium">{selectedDN.dn_number}</p>
                  <p className="text-sm text-muted-foreground">Client: {selectedDN.client_name}</p>
                  <p className="text-sm text-muted-foreground">Type: {selectedDN.dn_type}</p>
                  <p className="text-sm text-muted-foreground">Items: {selectedDN.items?.length || 0}</p>
                </CardContent>
              </Card>
            )}
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Trigger</Label>
                <Select value={createForm.trigger} onValueChange={(v) => setCreateForm({...createForm, trigger: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="client_request">Client Request</SelectItem>
                    <SelectItem value="admin_action">Admin Action</SelectItem>
                    <SelectItem value="contract_end">Contract End</SelectItem>
                    <SelectItem value="po_completion">PO Completion</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Requested Return Date</Label>
                <Input
                  type="date"
                  value={createForm.requested_return_date}
                  onChange={(e) => setCreateForm({...createForm, requested_return_date: e.target.value})}
                />
              </div>
            </div>
            
            <div>
              <Label>Return Location</Label>
              <Input
                value={createForm.return_location}
                onChange={(e) => setCreateForm({...createForm, return_location: e.target.value})}
                placeholder="Where should equipment be returned?"
              />
            </div>
            
            <div className="flex items-center gap-2">
              <Checkbox
                checked={createForm.transport_required}
                onCheckedChange={(c) => setCreateForm({...createForm, transport_required: c})}
              />
              <Label>Transport Required</Label>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Client Contact Name</Label>
                <Input
                  value={createForm.client_contact_name}
                  onChange={(e) => setCreateForm({...createForm, client_contact_name: e.target.value})}
                />
              </div>
              <div>
                <Label>Client Contact Phone</Label>
                <Input
                  value={createForm.client_contact_phone}
                  onChange={(e) => setCreateForm({...createForm, client_contact_phone: e.target.value})}
                />
              </div>
            </div>
            
            <div>
              <Label>Reason</Label>
              <Input
                value={createForm.reason}
                onChange={(e) => setCreateForm({...createForm, reason: e.target.value})}
                placeholder="Reason for demobilization"
              />
            </div>
            
            <div>
              <Label>Notes</Label>
              <Textarea
                value={createForm.notes}
                onChange={(e) => setCreateForm({...createForm, notes: e.target.value})}
                rows={2}
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateDemob} disabled={!selectedDN}>Create Demob Request</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Truck className="h-5 w-5" />
              {demobDetail?.demob_number}
            </DialogTitle>
          </DialogHeader>
          
          {demobDetail && (
            <Tabs defaultValue="overview">
              <TabsList className="grid grid-cols-4 w-full">
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="inspection">Inspection</TabsTrigger>
                <TabsTrigger value="damages">Damages ({demobDetail.damage_count || 0})</TabsTrigger>
                <TabsTrigger value="photos">Photos ({demobDetail.photos?.length || 0})</TabsTrigger>
              </TabsList>
              
              {/* Overview Tab */}
              <TabsContent value="overview" className="space-y-4 mt-4">
                <div className="flex items-center justify-between">
                  <Badge className={statusColors[demobDetail.status]}>{demobDetail.status.replace('_', ' ')}</Badge>
                  <div className="flex gap-2">
                    {demobDetail.status === 'requested' && (
                      <Button size="sm" onClick={() => { setScheduleForm({...scheduleForm, return_location: demobDetail.return_location}); setScheduleDialogOpen(true); }}>
                        <Calendar className="h-4 w-4 mr-1" />
                        Schedule
                      </Button>
                    )}
                    {['requested', 'scheduled'].includes(demobDetail.status) && (
                      <Button size="sm" onClick={handleStartInspection}>
                        <ClipboardCheck className="h-4 w-4 mr-1" />
                        Start Inspection
                      </Button>
                    )}
                    {demobDetail.status === 'in_progress' && (
                      <Button size="sm" onClick={() => setCompleteInspectionDialogOpen(true)}>
                        <CheckCircle className="h-4 w-4 mr-1" />
                        Complete Inspection
                      </Button>
                    )}
                    {demobDetail.status === 'pending_invoice' && (
                      <>
                        <Button size="sm" variant="outline" onClick={handleGenerateFinalInvoice}>
                          <FileText className="h-4 w-4 mr-1" />
                          Generate Invoice
                        </Button>
                        <Button size="sm" onClick={handleCompleteDemob}>
                          <CheckCircle className="h-4 w-4 mr-1" />
                          Complete Demob
                        </Button>
                      </>
                    )}
                    {!['completed', 'cancelled'].includes(demobDetail.status) && (
                      <Button size="sm" variant="destructive" onClick={handleCancelDemob}>
                        <XCircle className="h-4 w-4 mr-1" />
                        Cancel
                      </Button>
                    )}
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <Card>
                    <CardContent className="p-4 space-y-2">
                      <h4 className="font-semibold">Reference</h4>
                      <p className="text-sm"><span className="text-muted-foreground">DN:</span> {demobDetail.dn_number}</p>
                      <p className="text-sm"><span className="text-muted-foreground">Client PO:</span> {demobDetail.client_po_number || '-'}</p>
                      <p className="text-sm"><span className="text-muted-foreground">Type:</span> {demobDetail.dn_type}</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4 space-y-2">
                      <h4 className="font-semibold">Client</h4>
                      <p className="text-sm">{demobDetail.client_name}</p>
                      <p className="text-sm text-muted-foreground">{demobDetail.company_name}</p>
                      {demobDetail.client_contact_name && (
                        <p className="text-sm flex items-center gap-1">
                          <User className="h-3 w-3" /> {demobDetail.client_contact_name}
                        </p>
                      )}
                      {demobDetail.client_contact_phone && (
                        <p className="text-sm flex items-center gap-1">
                          <Phone className="h-3 w-3" /> {demobDetail.client_contact_phone}
                        </p>
                      )}
                    </CardContent>
                  </Card>
                </div>
                
                <Card>
                  <CardContent className="p-4">
                    <h4 className="font-semibold mb-3">Schedule & Return</h4>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <p className="text-muted-foreground">Requested Date</p>
                        <p>{demobDetail.requested_return_date || '-'}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Scheduled Date</p>
                        <p>{demobDetail.scheduled_return_date || '-'}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Actual Return</p>
                        <p>{demobDetail.actual_return_date || '-'}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Return Location</p>
                        <p>{demobDetail.return_location || '-'}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Transport</p>
                        <p>{demobDetail.transport_required ? 'Required' : 'Not Required'}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Overall Condition</p>
                        {demobDetail.overall_condition ? (
                          <Badge className={conditionColors[demobDetail.overall_condition]}>{demobDetail.overall_condition}</Badge>
                        ) : '-'}
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardContent className="p-4">
                    <h4 className="font-semibold mb-3">Items ({demobDetail.items?.length || 0})</h4>
                    <div className="space-y-2">
                      {demobDetail.items?.map((item, idx) => (
                        <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                          <span>{item.description}</span>
                          <Badge variant="outline">{item.quantity} {item.unit}</Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
              
              {/* Inspection Tab */}
              <TabsContent value="inspection" className="space-y-4 mt-4">
                <div className="flex justify-between">
                  <h4 className="font-semibold">Inspection Items</h4>
                  {demobDetail.status === 'in_progress' && (
                    <Button size="sm" onClick={() => setInspectionDialogOpen(true)}>
                      <Plus className="h-4 w-4 mr-1" />
                      Add Inspection
                    </Button>
                  )}
                </div>
                
                {demobDetail.inspections?.length > 0 ? (
                  <div className="space-y-3">
                    {demobDetail.inspections.map(insp => (
                      <Card key={insp.id}>
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="font-medium">{insp.item_description}</p>
                              <div className="flex items-center gap-4 mt-2 text-sm">
                                <span className="text-muted-foreground">Deployment:</span>
                                {insp.deployment_condition ? (
                                  <Badge className={conditionColors[insp.deployment_condition]}>{insp.deployment_condition}</Badge>
                                ) : '-'}
                                <ChevronRight className="h-4 w-4" />
                                <span className="text-muted-foreground">Return:</span>
                                <Badge className={conditionColors[insp.return_condition]}>{insp.return_condition}</Badge>
                              </div>
                              {insp.hours_used !== null && (
                                <p className="text-sm text-muted-foreground mt-1">
                                  Hours used: {insp.hours_used?.toFixed(1)}
                                </p>
                              )}
                              {insp.condition_notes && (
                                <p className="text-sm text-muted-foreground mt-1">{insp.condition_notes}</p>
                              )}
                            </div>
                            {insp.has_damage && (
                              <Badge className="bg-red-100 text-red-800">
                                <AlertTriangle className="h-3 w-3 mr-1" />
                                Damaged
                              </Badge>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    No inspection items yet
                  </div>
                )}
              </TabsContent>
              
              {/* Damages Tab */}
              <TabsContent value="damages" className="space-y-4 mt-4">
                <div className="flex justify-between">
                  <div>
                    <h4 className="font-semibold">Damage Records</h4>
                    {demobDetail.total_damage_cost > 0 && (
                      <p className="text-sm text-red-600">Total: SAR {demobDetail.total_damage_cost?.toLocaleString()}</p>
                    )}
                  </div>
                  {demobDetail.status === 'in_progress' && (
                    <Button size="sm" variant="destructive" onClick={() => setDamageDialogOpen(true)}>
                      <AlertTriangle className="h-4 w-4 mr-1" />
                      Record Damage
                    </Button>
                  )}
                </div>
                
                {demobDetail.damages?.length > 0 ? (
                  <div className="space-y-3">
                    {demobDetail.damages.map(damage => {
                      const status = damage.damage_status || 'pending_review';
                      const statusStyle = {
                        pending_review: 'bg-yellow-100 text-yellow-800',
                        approved: 'bg-blue-100 text-blue-800',
                        under_repair: 'bg-orange-100 text-orange-800',
                        completed: 'bg-green-100 text-green-800',
                        rejected: 'bg-gray-200 text-gray-700',
                      }[status] || 'bg-slate-100 text-slate-700';
                      return (
                        <Card key={damage.id} className="border-red-200" data-testid={`damage-card-${damage.id}`}>
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex-1">
                                <div className="flex flex-wrap items-center gap-2">
                                  {damage.inspection_item && (
                                    <Badge variant="outline" className="capitalize">{String(damage.inspection_item).replace(/_/g,' ')}</Badge>
                                  )}
                                  {damage.damage_type && (
                                    <Badge variant="outline" className="capitalize">{String(damage.damage_type).replace(/_/g,' ')}</Badge>
                                  )}
                                  <Badge variant="outline" className="capitalize">{damage.category}</Badge>
                                  <Badge className={severityColors[damage.severity]}>{damage.severity}</Badge>
                                  {damage.priority && (
                                    <Badge variant="outline" className="capitalize">Priority: {damage.priority}</Badge>
                                  )}
                                  <Badge className={`${statusStyle} capitalize`} data-testid={`damage-status-${damage.id}`}>{status.replace(/_/g,' ')}</Badge>
                                </div>
                                <p className="mt-2 font-medium">{damage.description}</p>
                                <div className="text-xs text-muted-foreground mt-1 space-x-3">
                                  {damage.location_on_asset && <span>Location: {damage.location_on_asset}</span>}
                                  {damage.quantity > 1 && <span>Qty: {damage.quantity}</span>}
                                  {damage.repair_vendor && <span>Vendor: {damage.repair_vendor}</span>}
                                  {damage.insurance_claim && <span>Insurance Claim</span>}
                                  {damage.warranty_applicable && <span>Under Warranty</span>}
                                </div>
                                <div className="text-xs text-muted-foreground mt-1">
                                  Recorded by {damage.recorded_by_email || damage.created_by} · {damage.recorded_at || damage.created_at}
                                </div>
                              </div>
                              <div className="text-right space-y-1 min-w-32">
                                <p className="font-bold text-red-600">SAR {(damage.actual_repair_cost ?? damage.estimated_repair_cost)?.toLocaleString()}</p>
                                {damage.actual_repair_cost != null && damage.actual_repair_cost !== damage.estimated_repair_cost && (
                                  <p className="text-xs text-muted-foreground line-through">Est: {damage.estimated_repair_cost?.toLocaleString()}</p>
                                )}
                                {damage.billable_to_client && (
                                  <Badge className="bg-red-100 text-red-800 text-xs">Charge {damage.charge_to || 'client'}</Badge>
                                )}
                                {damage.invoiced && <Badge className="bg-green-100 text-green-800 text-xs">Invoiced</Badge>}
                              </div>
                            </div>

                            {/* Role-gated actions */}
                            <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t">
                              {isOpsManager && status === 'pending_review' && (
                                <>
                                  <Button size="sm" variant="outline" className="text-green-700 border-green-300" onClick={async () => {
                                    try {
                                      await approveDemobDamage(demobDetail.id, damage.id, null);
                                      toast.success('Damage approved — Finance can now set billing');
                                      fetchDemobDetail(demobDetail.id);
                                    } catch (err) { toast.error(err.response?.data?.detail || 'Approve failed'); }
                                  }} data-testid={`approve-damage-${damage.id}`}>Approve</Button>
                                  <Button size="sm" variant="outline" className="text-red-700 border-red-300" onClick={async () => {
                                    const remarks = window.prompt('Reason for rejection?', '');
                                    try {
                                      await rejectDemobDamage(demobDetail.id, damage.id, remarks || 'Rejected by Ops Manager');
                                      toast.success('Damage rejected');
                                      fetchDemobDetail(demobDetail.id);
                                    } catch (err) { toast.error(err.response?.data?.detail || 'Reject failed'); }
                                  }} data-testid={`reject-damage-${damage.id}`}>Reject</Button>
                                </>
                              )}
                              {isFinance && ['approved', 'under_repair', 'completed'].includes(status) && (
                                <>
                                  <Button size="sm" variant="outline" onClick={async () => {
                                    try {
                                      await setDemobDamageBillable(demobDetail.id, damage.id, { billable_to_client: true, charge_to: 'client' });
                                      toast.success('Marked billable to Client');
                                      fetchDemobDetail(demobDetail.id);
                                    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
                                  }} data-testid={`billable-client-${damage.id}`}>Bill Client</Button>
                                  <Button size="sm" variant="outline" onClick={async () => {
                                    try {
                                      await setDemobDamageBillable(demobDetail.id, damage.id, { billable_to_client: false, charge_to: 'company' });
                                      toast.success('Absorbed by Company');
                                      fetchDemobDetail(demobDetail.id);
                                    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
                                  }}>Absorb (Company)</Button>
                                  <Button size="sm" variant="outline" onClick={async () => {
                                    try {
                                      await setDemobDamageBillable(demobDetail.id, damage.id, { billable_to_client: false, charge_to: 'supplier' });
                                      toast.success('Charged to Supplier');
                                      fetchDemobDetail(demobDetail.id);
                                    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
                                  }}>Charge Supplier</Button>
                                  <Button size="sm" variant="outline" onClick={async () => {
                                    try {
                                      await setDemobDamageBillable(demobDetail.id, damage.id, { billable_to_client: false, charge_to: 'insurance' });
                                      toast.success('Insurance claim');
                                      fetchDemobDetail(demobDetail.id);
                                    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
                                  }}>Insurance</Button>
                                </>
                              )}
                              {demobDetail.status !== 'completed' && (
                                <Button size="sm" variant="ghost" className="text-red-600 ml-auto" onClick={async () => {
                                  if (!window.confirm(`Delete damage record?`)) return;
                                  try {
                                    await deleteDemobDamage(demobDetail.id, damage.id);
                                    toast.success('Damage deleted');
                                    fetchDemobDetail(demobDetail.id);
                                  } catch (err) { toast.error(err.response?.data?.detail || 'Delete failed'); }
                                }} data-testid={`delete-damage-${damage.id}`}>
                                  <Trash2 className="h-3 w-3 mr-1" /> Delete
                                </Button>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <CheckCircle className="h-12 w-12 mx-auto mb-2 text-green-500" />
                    No damages recorded
                  </div>
                )}
              </TabsContent>
              
              {/* Photos Tab */}
              <TabsContent value="photos" className="space-y-4 mt-4">
                <div className="flex justify-between items-center">
                  <h4 className="font-semibold">Inspection Photos & Videos</h4>
                  {['in_progress', 'pending_invoice'].includes(demobDetail.status) && (
                    <>
                      <input
                        ref={photoInputRef}
                        type="file"
                        accept="image/*,video/mp4,video/quicktime,video/webm"
                        multiple
                        onChange={handlePhotoUpload}
                        className="hidden"
                      />
                      <Button size="sm" onClick={() => photoInputRef.current?.click()} disabled={uploadingPhoto} data-testid="upload-photo-btn">
                        <Camera className="h-4 w-4 mr-1" />
                        {uploadingPhoto ? `Uploading… ${uploadProgress}%` : 'Add Photo / Video'}
                      </Button>
                    </>
                  )}
                </div>

                {uploadingPhoto && (
                  <div className="w-full bg-slate-200 rounded-full h-2 overflow-hidden">
                    <div className="bg-blue-600 h-2 transition-all" style={{ width: `${uploadProgress}%` }} />
                  </div>
                )}

                {demobDetail.photos?.length > 0 ? (
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                    {demobDetail.photos.map(photo => {
                      const isVideo = photo.media_kind === 'video';
                      return (
                        <div key={photo.id} className="aspect-square bg-slate-100 rounded-lg overflow-hidden relative group cursor-pointer" onClick={() => openPhotoLightbox(photo, demobDetail.photos)} data-testid={`photo-${photo.id}`}>
                          {isVideo ? (
                            <div className="w-full h-full flex items-center justify-center bg-slate-900 text-white">
                              <Camera className="h-8 w-8 opacity-70" />
                            </div>
                          ) : (
                            <img
                              src={`/api/demobilization/${demobDetail.id}/photo/${photo.id}`}
                              alt={photo.original_filename}
                              className="w-full h-full object-cover"
                              loading="lazy"
                              onError={(e) => e.target.style.display = 'none'}
                            />
                          )}
                          <div className="absolute bottom-0 left-0 right-0 bg-black/60 p-1 flex items-center justify-between">
                            <Badge className="text-xs">{isVideo ? 'video' : photo.photo_type}</Badge>
                            {['requested','scheduled','in_progress'].includes(demobDetail.status) && (isOpsManager || role === 'operations' || role === 'operation_executive' || role === 'operation_coordinator') && (
                              <button
                                onClick={(e) => { e.stopPropagation(); handleDeletePhoto(photo.id); }}
                                className="text-white opacity-0 group-hover:opacity-100 transition-opacity px-1"
                                data-testid={`delete-photo-${photo.id}`}
                                title="Delete"
                              >
                                <Trash2 className="h-3 w-3" />
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <ImageIcon className="h-12 w-12 mx-auto mb-2 text-slate-300" />
                    No photos uploaded
                  </div>
                )}
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>

      {/* Schedule Dialog */}
      <Dialog open={scheduleDialogOpen} onOpenChange={setScheduleDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Schedule Demobilization</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Scheduled Return Date *</Label>
              <Input
                type="date"
                value={scheduleForm.scheduled_return_date}
                onChange={(e) => setScheduleForm({...scheduleForm, scheduled_return_date: e.target.value})}
              />
            </div>
            <div>
              <Label>Return Location</Label>
              <Input
                value={scheduleForm.return_location}
                onChange={(e) => setScheduleForm({...scheduleForm, return_location: e.target.value})}
              />
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                checked={scheduleForm.transport_required}
                onCheckedChange={(c) => setScheduleForm({...scheduleForm, transport_required: c})}
              />
              <Label>Transport Required</Label>
            </div>
            {scheduleForm.transport_required && (
              <>
                <div>
                  <Label>Transport Vehicle</Label>
                  <Input
                    value={scheduleForm.transport_vehicle}
                    onChange={(e) => setScheduleForm({...scheduleForm, transport_vehicle: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Driver</Label>
                  <Input
                    value={scheduleForm.transport_driver}
                    onChange={(e) => setScheduleForm({...scheduleForm, transport_driver: e.target.value})}
                  />
                </div>
              </>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setScheduleDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleScheduleDemob}>Schedule</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Inspection Dialog */}
      <Dialog open={inspectionDialogOpen} onOpenChange={setInspectionDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Inspection Item</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Item Description *</Label>
              <Input
                value={inspectionForm.item_description}
                onChange={(e) => setInspectionForm({...inspectionForm, item_description: e.target.value})}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Deployment Condition</Label>
                <Select value={inspectionForm.deployment_condition} onValueChange={(v) => setInspectionForm({...inspectionForm, deployment_condition: v})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="good">Good</SelectItem>
                    <SelectItem value="fair">Fair</SelectItem>
                    <SelectItem value="damaged">Damaged</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Return Condition *</Label>
                <Select value={inspectionForm.return_condition} onValueChange={(v) => setInspectionForm({...inspectionForm, return_condition: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="good">Good</SelectItem>
                    <SelectItem value="fair">Fair</SelectItem>
                    <SelectItem value="damaged">Damaged</SelectItem>
                    <SelectItem value="needs_repair">Needs Repair</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Deployment Hours</Label>
                <Input
                  type="number"
                  value={inspectionForm.deployment_hours}
                  onChange={(e) => setInspectionForm({...inspectionForm, deployment_hours: e.target.value})}
                />
              </div>
              <div>
                <Label>Return Hours</Label>
                <Input
                  type="number"
                  value={inspectionForm.return_hours}
                  onChange={(e) => setInspectionForm({...inspectionForm, return_hours: e.target.value})}
                />
              </div>
            </div>
            <div>
              <Label>Condition Notes</Label>
              <Textarea
                value={inspectionForm.condition_notes}
                onChange={(e) => setInspectionForm({...inspectionForm, condition_notes: e.target.value})}
                rows={2}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setInspectionDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleAddInspection}>Add Inspection</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Damage Dialog */}
      <Dialog open={damageDialogOpen} onOpenChange={setDamageDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="damage-dialog">
          <DialogHeader>
            <DialogTitle>Record Damage</DialogTitle>
            <p className="text-xs text-muted-foreground">
              Inspectors record damage only. Ops Manager reviews and approves. Finance decides billing.
              Photos are mandatory.
            </p>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Inspection Item *</Label>
                <Select value={damageForm.inspection_item} onValueChange={(v) => setDamageForm({...damageForm, inspection_item: v})}>
                  <SelectTrigger data-testid="damage-inspection-item"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="engine">Engine</SelectItem>
                    <SelectItem value="tyres">Tyres</SelectItem>
                    <SelectItem value="cabin">Cabin</SelectItem>
                    <SelectItem value="hydraulic">Hydraulic System</SelectItem>
                    <SelectItem value="boom">Boom</SelectItem>
                    <SelectItem value="chassis">Chassis</SelectItem>
                    <SelectItem value="electrical">Electrical</SelectItem>
                    <SelectItem value="glass">Glass</SelectItem>
                    <SelectItem value="accessories">Accessories</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Damage Type *</Label>
                <Select value={damageForm.damage_type} onValueChange={(v) => setDamageForm({...damageForm, damage_type: v})}>
                  <SelectTrigger data-testid="damage-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="scratch">Scratch</SelectItem>
                    <SelectItem value="dent">Dent</SelectItem>
                    <SelectItem value="crack">Crack</SelectItem>
                    <SelectItem value="broken">Broken</SelectItem>
                    <SelectItem value="oil_leak">Oil Leak</SelectItem>
                    <SelectItem value="mechanical_failure">Mechanical Failure</SelectItem>
                    <SelectItem value="electrical_fault">Electrical Fault</SelectItem>
                    <SelectItem value="missing_part">Missing Part</SelectItem>
                    <SelectItem value="tyre_damage">Tyre Damage</SelectItem>
                    <SelectItem value="glass_damage">Glass Damage</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Category *</Label>
                <Select value={damageForm.category} onValueChange={(v) => setDamageForm({...damageForm, category: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="structural">Structural</SelectItem>
                    <SelectItem value="mechanical">Mechanical</SelectItem>
                    <SelectItem value="electrical">Electrical</SelectItem>
                    <SelectItem value="cosmetic">Cosmetic</SelectItem>
                    <SelectItem value="wear_and_tear">Wear and Tear</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Severity *</Label>
                <Select value={damageForm.severity} onValueChange={(v) => setDamageForm({...damageForm, severity: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="minor">Minor</SelectItem>
                    <SelectItem value="moderate">Moderate</SelectItem>
                    <SelectItem value="major">Major</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Priority *</Label>
                <Select value={damageForm.priority} onValueChange={(v) => setDamageForm({...damageForm, priority: v})}>
                  <SelectTrigger data-testid="damage-priority"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Description *</Label>
              <Textarea
                value={damageForm.description}
                onChange={(e) => setDamageForm({...damageForm, description: e.target.value})}
                rows={2}
                data-testid="damage-description"
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Location on Asset</Label>
                <Input
                  value={damageForm.location_on_asset}
                  onChange={(e) => setDamageForm({...damageForm, location_on_asset: e.target.value})}
                  placeholder="e.g., Front left corner"
                />
              </div>
              <div>
                <Label>Quantity</Label>
                <Input
                  type="number" min={1}
                  value={damageForm.quantity}
                  onChange={(e) => setDamageForm({...damageForm, quantity: e.target.value})}
                />
              </div>
              <div>
                <Label>Repair Vendor</Label>
                <Input
                  value={damageForm.repair_vendor}
                  onChange={(e) => setDamageForm({...damageForm, repair_vendor: e.target.value})}
                  placeholder="Optional"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Estimated Repair Cost (SAR)</Label>
                <Input type="number" value={damageForm.estimated_repair_cost}
                  onChange={(e) => setDamageForm({...damageForm, estimated_repair_cost: e.target.value})}
                  data-testid="damage-est-cost" />
              </div>
              <div>
                <Label>Actual Repair Cost (SAR) — optional</Label>
                <Input type="number" value={damageForm.actual_repair_cost}
                  onChange={(e) => setDamageForm({...damageForm, actual_repair_cost: e.target.value})} />
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-6">
              <div className="flex items-center gap-2">
                <Checkbox checked={damageForm.insurance_claim}
                  onCheckedChange={(c) => setDamageForm({...damageForm, insurance_claim: c})} />
                <Label>Insurance Claim</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox checked={damageForm.warranty_applicable}
                  onCheckedChange={(c) => setDamageForm({...damageForm, warranty_applicable: c})} />
                <Label>Under Warranty</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox checked={damageForm.repair_required}
                  onCheckedChange={(c) => setDamageForm({...damageForm, repair_required: c})} />
                <Label>Repair Required</Label>
              </div>
            </div>
            <div>
              <Label>Internal Notes / Remarks</Label>
              <Textarea rows={2} value={damageForm.internal_notes}
                onChange={(e) => setDamageForm({...damageForm, internal_notes: e.target.value})} />
            </div>
            <div>
              <Label>Damage Photos * <span className="text-xs text-muted-foreground">(at least one photo is mandatory)</span></Label>
              <Input type="file" accept="image/*,video/mp4,video/quicktime,video/webm" multiple
                onChange={(e) => setDamagePhotoFiles(Array.from(e.target.files || []))}
                data-testid="damage-photos-input" />
              {damagePhotoFiles.length > 0 && (
                <p className="text-xs text-muted-foreground mt-1">{damagePhotoFiles.length} file(s) selected</p>
              )}
            </div>
            <div className="p-2 rounded bg-amber-50 text-xs text-amber-800">
              Note: Billable status (&quot;Charge To&quot;) is decided by Finance <em>after</em> Ops Manager approves this record.
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setDamageDialogOpen(false)}>Cancel</Button>
              <Button variant="destructive" onClick={handleAddDamage} data-testid="save-damage-btn">Record Damage</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Complete Inspection Dialog */}
      <Dialog open={completeInspectionDialogOpen} onOpenChange={(o) => { setCompleteInspectionDialogOpen(o); if (o && demobDetail?.id) getDemobReadiness(demobDetail.id).then(r => setReadiness(r.data)).catch(() => {}); }}>
        <DialogContent data-testid="complete-inspection-dialog">
          <DialogHeader>
            <DialogTitle>Complete Inspection</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {readiness.missing?.length > 0 && (
              <div className="p-3 rounded border border-amber-300 bg-amber-50 text-sm">
                <div className="font-semibold text-amber-800 mb-1">Please resolve before completing:</div>
                <ul className="list-disc pl-5 text-amber-900 space-y-0.5">
                  {readiness.missing.map((m, i) => <li key={i}>{m}</li>)}
                </ul>
              </div>
            )}
            <div>
              <Label>Overall Condition *</Label>
              <Select value={completeForm.overall_condition} onValueChange={(v) => setCompleteForm({...completeForm, overall_condition: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="good">Good</SelectItem>
                  <SelectItem value="fair">Fair</SelectItem>
                  <SelectItem value="damaged">Damaged</SelectItem>
                  <SelectItem value="needs_repair">Needs Repair</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Client Representative Name</Label>
              <Input
                value={completeForm.client_signed_by}
                onChange={(e) => setCompleteForm({...completeForm, client_signed_by: e.target.value})}
                placeholder="Name of person who signed off"
              />
            </div>
            <div>
              <Label>Inspection Notes</Label>
              <Textarea
                value={completeForm.inspection_notes}
                onChange={(e) => setCompleteForm({...completeForm, inspection_notes: e.target.value})}
                rows={3}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setCompleteInspectionDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleCompleteInspection} disabled={!readiness.ready} data-testid="submit-complete-inspection">
                <CheckCircle className="h-4 w-4 mr-1" />
                Complete Inspection
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Photo/Video Lightbox */}
      <Dialog open={lightbox.open} onOpenChange={(o) => { setLightbox({ ...lightbox, open: o }); setZoom(1); }}>
        <DialogContent className="max-w-5xl bg-black/95 border-0 text-white p-2" data-testid="photo-lightbox">
          <DialogHeader>
            <DialogTitle className="text-white text-sm font-normal">
              {lightbox.current?.original_filename} <span className="text-slate-400">· {lightbox.current?.uploaded_by_email} · {lightbox.current?.uploaded_at}</span>
            </DialogTitle>
          </DialogHeader>
          {lightbox.current && (
            <div className="flex items-center justify-center bg-black rounded overflow-auto" style={{ minHeight: '60vh', maxHeight: '75vh' }}>
              {lightbox.current.media_kind === 'video' ? (
                <video src={lightbox.current.dataUrl} controls className="max-h-[70vh]" />
              ) : (
                <img src={lightbox.current.dataUrl} alt={lightbox.current.original_filename} style={{ transform: `scale(${zoom})`, transition: 'transform 0.15s' }} className="max-h-[70vh]" />
              )}
            </div>
          )}
          <div className="flex items-center justify-between text-xs mt-2 px-2">
            <div className="flex gap-2">
              <Button size="sm" variant="outline" className="text-black" onClick={() => navLightbox(-1)} data-testid="lightbox-prev" disabled={lightbox.index <= 0}>← Prev</Button>
              <Button size="sm" variant="outline" className="text-black" onClick={() => navLightbox(1)} data-testid="lightbox-next" disabled={lightbox.index >= lightbox.photos.length - 1}>Next →</Button>
              {lightbox.current?.media_kind !== 'video' && (
                <>
                  <Button size="sm" variant="outline" className="text-black" onClick={() => setZoom(z => Math.min(z + 0.25, 4))}>Zoom +</Button>
                  <Button size="sm" variant="outline" className="text-black" onClick={() => setZoom(z => Math.max(z - 0.25, 0.5))}>Zoom −</Button>
                  <Button size="sm" variant="outline" className="text-black" onClick={() => setZoom(1)}>Reset</Button>
                </>
              )}
            </div>
            <div className="text-slate-400">
              {lightbox.index + 1} / {lightbox.photos.length}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DemobilizationPage;
